import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import companyService, {
  type CompanyProfile,
  type CompanyAuthResponse,
  type StorefrontFeatures,
} from "../services/companyService";
import {
  DEFAULT_THEME,
  resolveTheme,
  type ThemePalette,
} from "../theme/palette";
// Re-export so existing imports from companySlice continue to work
export type { ThemePalette } from "../theme/palette";
export { DEFAULT_THEME } from "../theme/palette";

interface CompanyState {
  id: string | null;
  name: string;
  nameAr: string | null;
  slug: string | null;
  email: string;
  sectorType: string;
  language: "ar" | "en";
  theme: ThemePalette;
  logoUrl: string | null;
  faviconUrl: string | null;
  logoMediaId: string | null;
  faviconMediaId: string | null;
  phone: string | null;
  address: string | null;
  taxNumber: string | null;
  currencyCode: string;
  storefrontFeatures: StorefrontFeatures;
  aiAssistantEnabled: boolean;
  adminAiAssistantEnabled: boolean;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

export type AppLanguage = "ar" | "en";

function getStoredLanguage(): AppLanguage | null {
  if (typeof localStorage === "undefined") return null;
  const language = localStorage.getItem("language");
  return language === "ar" || language === "en" ? language : null;
}

const initialState: CompanyState = {
  id: null,
  name: "",
  nameAr: null,
  slug: null,
  email: "",
  sectorType: "",
  language: getStoredLanguage() ?? "ar",
  theme: DEFAULT_THEME,
  logoUrl: null,
  faviconUrl: null,
  logoMediaId: null,
  faviconMediaId: null,
  phone: null,
  address: null,
  taxNumber: null,
  currencyCode: "JOD",
  storefrontFeatures: {
    freeShipping: true,
    easyReturns: true,
    securePayment: true,
  },
  aiAssistantEnabled: true,
  adminAiAssistantEnabled: true,
  isAuthenticated: false,
  loading: false,
  error: null,
};

function applyProfile(
  state: CompanyState,
  company: CompanyProfile,
  useProfileLanguage = false,
) {
  state.id = company.id;
  state.name = company.name;
  state.nameAr = company.nameAr;
  state.slug = company.slug;
  state.email = company.email;
  state.sectorType = company.sectorType;
  if (useProfileLanguage && !getStoredLanguage()) {
    state.language = company.languageCode === "en" ? "en" : "ar";
    localStorage.setItem("language", state.language);
  }
  state.logoUrl = company.logoMediaUrl ?? company.logoUrl;
  state.faviconUrl = company.faviconMediaUrl ?? company.faviconUrl;
  state.logoMediaId = company.logoMediaId;
  state.faviconMediaId = company.faviconMediaId;
  state.phone = company.phone;
  state.address = company.address;
  state.taxNumber = company.taxNumber;
  state.currencyCode = company.currencyCode;
  state.storefrontFeatures = {
    ...state.storefrontFeatures,
    ...company.storefrontFeatures,
  };
  state.aiAssistantEnabled = company.aiAssistantEnabled !== false;
  state.adminAiAssistantEnabled = company.adminAiAssistantEnabled !== false;
  state.theme = resolveTheme(company.theme);
}

let pendingLanguageUpdate: {
  language: AppLanguage;
  request: Promise<CompanyProfile>;
} | null = null;

function requestLanguageUpdate(language: AppLanguage): Promise<CompanyProfile> {
  if (pendingLanguageUpdate?.language === language) {
    return pendingLanguageUpdate.request;
  }
  const request = companyService
    .updateLanguage(language)
    .then((response) => response.data.data as CompanyProfile);
  pendingLanguageUpdate = { language, request };
  const clearPending = () => {
    if (pendingLanguageUpdate?.request === request)
      pendingLanguageUpdate = null;
  };
  void request.then(clearPending, clearPending);
  return request;
}

async function syncStoredLanguage(
  company: CompanyProfile,
): Promise<CompanyProfile> {
  const storedLanguage = getStoredLanguage();
  if (!storedLanguage || company.languageCode === storedLanguage)
    return company;
  try {
    return await requestLanguageUpdate(storedLanguage);
  } catch {
    return company;
  }
}

function storeCompanySession(response: CompanyAuthResponse) {
  localStorage.setItem("accessToken", response.tokens.accessToken);
  localStorage.setItem("refreshToken", response.tokens.refreshToken);
  localStorage.setItem("principalType", "company");
}

function getErrorMessage(err: unknown, fallback: string): string {
  try {
    const msg = (
      err as { response?: { data?: { message?: string | string[] } } }
    )?.response?.data?.message;
    if (Array.isArray(msg)) return msg.join(". ") || fallback;
    if (typeof msg === "string") return msg;
    return fallback;
  } catch {
    return fallback;
  }
}

export const loginCompany = createAsyncThunk<
  CompanyAuthResponse,
  { email: string; password: string },
  { rejectValue: string }
>("company/login", async (credentials, { rejectWithValue }) => {
  try {
    const res = await companyService.login(credentials);
    const data = res.data.data;
    storeCompanySession(data);
    return syncStoredLanguage(data.company).then((company) => ({
      ...data,
      company,
    }));
  } catch (err: unknown) {
    return rejectWithValue(getErrorMessage(err, "Login failed"));
  }
});

export const fetchCompanyProfile = createAsyncThunk<
  CompanyProfile,
  { silent?: boolean } | undefined,
  { rejectValue: string }
>("company/fetchProfile", async (options, { rejectWithValue }) => {
  try {
    const res = await companyService.getProfile({
      skipAuthRedirect: options?.silent === true,
    });
    const company = res.data.data;
    return localStorage.getItem("principalType") === "company"
      ? await syncStoredLanguage(company)
      : company;
  } catch (err: unknown) {
    return rejectWithValue(getErrorMessage(err, "Failed to fetch profile"));
  }
});

export const updateLanguage = createAsyncThunk<
  void,
  AppLanguage,
  { rejectValue: string }
>(
  "company/updateLanguage",
  async (languageCode, { dispatch, getState, rejectWithValue }) => {
    dispatch(setLanguage(languageCode));
    const state = getState() as {
      company: CompanyState;
      auth: { principalType: "company" | "user" | null };
    };
    const principalType =
      state.auth.principalType ?? localStorage.getItem("principalType");
    if (principalType !== "company" || !state.company.id) return;
    try {
      await requestLanguageUpdate(languageCode);
    } catch (err: unknown) {
      return rejectWithValue(getErrorMessage(err, "Failed to update language"));
    }
  },
);

export const updateTheme = createAsyncThunk<
  CompanyProfile,
  Partial<ThemePalette>,
  { rejectValue: string }
>("company/updateTheme", async (theme, { rejectWithValue }) => {
  try {
    const res = await companyService.updateTheme({ ...theme });
    return res.data.data;
  } catch (err: unknown) {
    return rejectWithValue(getErrorMessage(err, "Failed to update theme"));
  }
});

const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {
    setLanguage(state, action: PayloadAction<AppLanguage>) {
      state.language = action.payload;
      localStorage.setItem("language", action.payload);
    },
    setTheme(state, action: PayloadAction<Partial<ThemePalette>>) {
      state.theme = { ...state.theme, ...action.payload };
    },
    setCompanyProfile(state, action: PayloadAction<CompanyProfile>) {
      applyProfile(state, action.payload);
    },
    logout(state) {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("principalType");
      localStorage.removeItem("roleType");
      Object.assign(state, { ...initialState, isAuthenticated: false });
    },
  },
  extraReducers: (builder) => {
    const handleAuth = (
      state: CompanyState,
      action: PayloadAction<CompanyAuthResponse>,
    ) => {
      state.loading = false;
      state.error = null;
      state.isAuthenticated = true;
      const { accessToken, refreshToken } = action.payload.tokens;
      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("principalType", "company");
      localStorage.removeItem("roleType");
      applyProfile(state, action.payload.company, true);
    };

    builder
      .addCase(loginCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginCompany.fulfilled, handleAuth)
      .addCase(loginCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Login failed";
      })

      .addCase(fetchCompanyProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCompanyProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        applyProfile(state, action.payload, true);
        if (localStorage.getItem("principalType") === "company") {
          state.isAuthenticated = true;
        }
      })
      .addCase(fetchCompanyProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to fetch profile";
        // Do not clear tokens or isAuthenticated — token may be a user token; auth guard will try user profile.
      })

      .addCase(updateTheme.fulfilled, (state, action) => {
        applyProfile(state, action.payload);
      })
      .addMatcher(
        (action) =>
          action.type === "auth/loginUser/fulfilled" ||
          action.type === "auth/setAuthUser",
        (state) => {
          state.isAuthenticated = false;
          state.loading = false;
          state.error = null;
        },
      )
      .addMatcher(
        (action) =>
          action.type === "auth/initialize/fulfilled" &&
          action.payload !== "company",
        (state) => {
          state.isAuthenticated = false;
        },
      )
      .addMatcher(
        (action) => action.type === "auth/initialize/rejected",
        (state) => {
          state.isAuthenticated = false;
        },
      );
  },
});

export const { setLanguage, setTheme, setCompanyProfile, logout } =
  companySlice.actions;
export default companySlice.reducer;
